import { AlertCircleIcon, Button } from "@wato/ui";
import type { ConsoleMessageView } from "../../../api/console-types";
import { asRecord, rowText, str, type Tr } from "./console-text";

/** MC-08: the plain-language "before → after" a manager approves, rejects or lets expire. */
export function ProposalCard({
  m,
  tr,
  locale,
  busy,
  onDecide,
}: {
  m: ConsoleMessageView;
  tr: Tr;
  locale: string;
  busy: boolean;
  onDecide: (verb: "approve" | "reject") => void;
}) {
  const p = m.payload;
  const summary = asRecord(p.summary);
  const rows = Array.isArray(p.rows) ? (p.rows as Record<string, unknown>[]) : [];
  const status = str(p.status) || "pending";
  const label = (l: string) => (l.startsWith("dashboard.console.") ? tr(l.slice(18)) : l);
  const text = str(summary[locale === "ar" ? "ar" : "en"]);
  return (
    <div className="flex flex-col gap-2" data-testid="console-proposal" data-status={status}>
      <p className="flex items-center gap-2 font-semibold">
        <AlertCircleIcon aria-hidden="true" className="size-4 shrink-0 text-warning-fg" />
        {tr("proposal.header")}
      </p>
      {text && <p>{text}</p>}
      <ul className="flex flex-col gap-2">
        {rows.map((r, i) => (
          <li key={i} className="flex flex-col gap-1">
            <span className="text-xs font-medium text-fg-muted">{label(str(r.label))}</span>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-md bg-surface-muted px-2 py-1">
                <span className="block text-xs text-fg-muted">{tr("proposal.now")}</span>
                <span>{rowText(tr, (r.before as string | null) ?? null)}</span>
              </div>
              <div className="rounded-md bg-accent-soft px-2 py-1">
                <span className="block text-xs text-fg-muted">{tr("proposal.afterApproval")}</span>
                <span className="font-medium">
                  {rowText(tr, (r.after as string | null) ?? null)}
                </span>
              </div>
            </div>
          </li>
        ))}
      </ul>
      <p className="text-xs text-fg-muted">{tr(`proposal.status.${status}`)}</p>
      {status === "pending" && (
        <div className="flex gap-2">
          <Button size="sm" loading={busy} onClick={() => onDecide("approve")}>
            {tr("proposal.approve")}
          </Button>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => onDecide("reject")}>
            {tr("proposal.reject")}
          </Button>
        </div>
      )}
    </div>
  );
}
